"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, Navigate } from "react-router-dom"
import { Row, Col, FormGroup, Input, Button, Label, Modal, ModalHeader, ModalBody, Card, CardBody } from "reactstrap"
import Services from "./services.js"
import AuthService from "../Authentication/auth.service.js"
import LoadingOverlay from "react-loading-overlay-ts"
import DNALoader from "../../components/Loaders/DnaLoader.js"
import Alerts from "../../components/Alerts/alerts.js"
import PermissionAccess from "../../PermissionAccess/Access.js"

const UpdateDoctor = ({ customer, onSuccess, onCancel }) => {
  const [modal, setModal] = useState(true)
  const [Doctor, setDoctor] = useState({
    ...customer,
    Phone: customer.phone || customer.Phone || "",
    mmc_number: customer.mmc_number || customer.mmc || "",
    apc_number: customer.apc_number || customer.apc || "",
  })
  const [countryCode, setCountryCode] = useState('+60')
  const [phoneLocal, setPhoneLocal] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [loginNavigate, setLoginNavigate] = useState(false)
  const { authData } = PermissionAccess()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem("language"))
  }, [i18n])

  useEffect(() => {
    try {
      const raw = Doctor.Phone || ""
      if (raw.startsWith('+')) {
        const match = raw.match(/^(\+\d{1,3})(.*)$/)
        if (match) {
          setCountryCode(match[1])
          setPhoneLocal(match[2])
        } else {
          setPhoneLocal(raw)
        }
      } else {
        setPhoneLocal(raw)
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDoctorChange = (field, value) => {
    setDoctor((prev) => ({ ...prev, [field]: value }))
  }

  const validateForm = () => {
    return (
      Doctor.name &&
      Doctor.email &&
      (phoneLocal || Doctor.Phone) &&
      Doctor.Address
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      Alerts.swalErrorAlert("Please fill all fields.")
      return
    }

    if (password && password !== confirmPassword) {
      Alerts.swalErrorAlert(t('Passwords do not match'))
      return
    }

    const updatePhone = `${countryCode}${phoneLocal || ''}`

    const payload = {
      functionName: 'UpdateCustomer',
      postData: {
        _id: Doctor._id,
        name: Doctor.name,
        email: Doctor.email,
        phone: updatePhone,
        address: Doctor.Address,
        mmc_number: Doctor.mmc_number,
        apc_number: Doctor.apc_number,
        ...(password ? { password: btoa(password) } : {})
      }
    }

    setLoading(true)
    try {
      const response = await Services.UpdateCustomer(authData.sessionID, payload)
      setLoading(false)
      if (response.data.status) {
        onSuccess()
        Alerts.swalSuccessAlert(response.data.message || "Customer Updated Successfully.").then(() => {
          setModal(false)
        })
      } else {
        if (response.data.message === "Unauthorized") {
          AuthService.logout()
          setLoginNavigate(true)
        } else {
          Alerts.swalErrorAlert(
            response.data.message || "Error: Can't Update Customer at the moment. Please try again later.",
          )
        }
      }
    } catch (error) {
      setLoading(false)
      Alerts.swalErrorAlert("Network Operation failed.")
    }
  }

  if (loginNavigate) {
    return <Navigate to={"/login"} />
  }

  return (
    <Modal size="lg" isOpen={modal} toggle={() => setModal(false)} backdrop={true}>
      <LoadingOverlay active={loading} spinner={<DNALoader />}>
        <ModalHeader>{t("Update Customer")}</ModalHeader>
        <ModalBody>
          <Card className="main-card mb-3">
            <CardBody>
              <form onSubmit={handleSubmit}>
                <FormGroup className="mb-3">
                  <Row>
                    <Col md="6">
                      <Label>{t("Customer Name")}</Label>
                      <Input
                        type="text"
                        value={Doctor.name}
                        onChange={(e) => handleDoctorChange("name", e.target.value)}
                        placeholder={t("Enter Doctor Customer")}
                      />
                    </Col>
                    <Col md="6">
                      <Label>{t("Email")}</Label>
                      <Input
                        type="email"
                        value={Doctor.email}
                        onChange={(e) => handleDoctorChange("email", e.target.value)}
                        placeholder={t("Enter Email")}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <Label>{t("Phone")}</Label>
                      <div className="d-flex">
                        <Input
                          type="text"
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          style={{ maxWidth: 100, marginRight: 8 }}
                        />
                        <Input
                          type="tel"
                          value={phoneLocal}
                          onChange={(e) => setPhoneLocal(e.target.value)}
                          placeholder={t("Enter Phone Number")}
                        />
                      </div>
                    </Col>
                    <Col md="6">
                      <Label>{t("Address (Google Location)")}</Label>
                      <Input
                        type="text"
                        value={Doctor.Address}
                        onChange={(e) => handleDoctorChange("Address", e.target.value)}
                        placeholder={t("Enter Address")}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="6">
                      <Label>{t("MMC Number")}</Label>
                      <Input
                        type="text"
                        value={Doctor.mmc_number}
                        onChange={(e) => handleDoctorChange("mmc_number", e.target.value)}
                        placeholder={t("Enter MMC Number")}
                      />
                    </Col>
                    <Col md="6">
                      <Label>{t("APC Number")}</Label>
                      <Input
                        type="text"
                        value={Doctor.apc_number}
                        onChange={(e) => handleDoctorChange("apc_number", e.target.value)}
                        placeholder={t("Enter APC Number")}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="6">
                      <Label>{t("Password")}</Label>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("Enter new password (optional)")}
                      />
                    </Col>
                    <Col md="6">
                      <Label>{t("Confirm Password")}</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t("Confirm new password")}
                      />
                    </Col>
                  </Row>
                  <div className="text-center mt-3">
                    <Button type="submit" color="success" size="lg" className="me-3">
                      {t("Update")}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      size="lg"
                      onClick={() => {
                        setModal(false)
                        onCancel()
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                  </div>
                </FormGroup>
              </form>
            </CardBody>
          </Card>
        </ModalBody>
      </LoadingOverlay>
    </Modal>
  )
}

export default UpdateDoctor

